import type { Book } from '@legere/storage-manager';
import { MarkdownRenderer } from '@legere/markdown-renderer';
import { robotoFonts } from './fonts/roboto';

const PDF_FONT_NAME = 'Roboto';

export async function exportToTxt(book: Book): Promise<Blob> {
  const renderer = new MarkdownRenderer();
  const html = renderer.render(book.content);
  const text = htmlToPlainText(html);

  return new Blob([text], { type: 'text/plain;charset=utf-8' });
}

export async function exportToPdf(book: Book): Promise<Blob> {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF();

  await ensurePdfFonts(doc);
  const lang = book.metadata.language || 'en';
  // Only set language if it's a valid jsPDF language code
  if (typeof (doc as any).setLanguage === 'function') {
    try {
      (doc as any).setLanguage(lang);
    } catch (e) {
      // Language not supported, skip
    }
  }

  const renderer = new MarkdownRenderer();
  const html = renderer.render(book.content);
  const text = htmlToPlainText(html, { headingBreaks: true });

  const margin = 20;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const maxWidth = pageWidth - 2 * margin;

  let yPos = margin;

  // Title
  doc.setFont(PDF_FONT_NAME, 'bold');
  doc.setFontSize(20);
  const titleLines = doc.splitTextToSize(book.title, maxWidth);
  for (const line of titleLines) {
    doc.text(line, margin, yPos);
    yPos += 8;
  }
  yPos += 4;

  // Author
  if (book.metadata.author) {
    doc.setFont(PDF_FONT_NAME, 'normal');
    doc.setFontSize(12);
    doc.text(`by ${book.metadata.author}`, margin, yPos);
    yPos += 10;
  }

  // Body
  doc.setFont(PDF_FONT_NAME, 'normal');
  doc.setFontSize(11);
  yPos += 4;

  const paragraphs = text.split('\n\n').filter((para) => para.trim().length > 0);

  for (const para of paragraphs) {
    const lines = doc.splitTextToSize(para, maxWidth);
    for (const line of lines) {
      if (yPos > pageHeight - margin) {
        doc.addPage();
        await ensurePdfFonts(doc);
        doc.setFont(PDF_FONT_NAME, 'normal');
        doc.setFontSize(11);
        yPos = margin;
      }
      doc.text(line, margin, yPos);
      yPos += 6;
    }
    yPos += 4;
  }

  return doc.output('blob');
}

export async function exportToEpub(book: Book): Promise<Blob> {
  const epub = (await import('epub-gen-memory')).default;

  const renderer = new MarkdownRenderer();
  const html = renderer.render(book.content);

  const options = {
    title: book.title,
    author: book.metadata.author || 'Unknown',
    css: `
      body {
        font-family: serif;
        line-height: 1.6;
        margin: 1em;
      }
      h1, h2, h3, h4, h5, h6 {
        margin-top: 1.5em;
        margin-bottom: 0.5em;
      }
      p {
        margin: 1em 0;
      }
      code {
        background-color: #f4f4f4;
        padding: 2px 4px;
        border-radius: 3px;
      }
      pre {
        background-color: #f4f4f4;
        padding: 1em;
        border-radius: 5px;
        overflow-x: auto;
      }
    `,
  };

  const content = [
    {
      title: book.title,
      content: html,
    },
  ];

  const buffer = await epub(options, content);
  // Convert Buffer to Uint8Array for Blob
  const uint8Array = new Uint8Array(buffer);
  return new Blob([uint8Array], { type: 'application/epub+zip' });
}

export async function exportToFb2Zip(book: Book): Promise<Blob> {
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();

  const fb2Content = convertMarkdownToFb2(book);
  const filename = book.title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  zip.file(`${filename}.fb2`, fb2Content);

  return zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 9 },
  });
}

function convertMarkdownToFb2(book: Book): string {
  const sections = extractSections(book.content);
  const language = book.metadata.language || 'en';
  const genre = (book.metadata.tags && book.metadata.tags[0]) || 'prose';
  const authorFull = book.metadata.author?.trim() || 'Unknown';
  const { firstName, lastName } = splitAuthorName(authorFull);
  const { assetIdMap, binaryEntries, coverImageId } = prepareImageAssets(book);

  let fb2 = `<?xml version="1.0" encoding="UTF-8"?>
<FictionBook xmlns="http://www.gribuser.ru/xml/fictionbook/2.0" xmlns:xlink="http://www.w3.org/1999/xlink">
  <description>
    <title-info>
      <genre>${escapeXml(genre)}</genre>
      <author>
        <first-name>${escapeXml(firstName)}</first-name>`;

  if (lastName) {
    fb2 += `
        <last-name>${escapeXml(lastName)}</last-name>`;
  }

  fb2 += `
      </author>
      <book-title>${escapeXml(book.title)}</book-title>
      <lang>${escapeXml(language)}</lang>`;

  if (coverImageId) {
    fb2 += `
      <coverpage>
        <image xlink:href="#${coverImageId}"/>
      </coverpage>`;
  }

  if (book.metadata.description) {
    fb2 += `
      <annotation>
        <p>${convertMarkdownInlineToFb2(book.metadata.description)}</p>
      </annotation>`;
  }

  fb2 += `
    </title-info>
    <document-info>
      <author>
        <nickname>Legere</nickname>
      </author>
      <date>${new Date().toISOString().split('T')[0]}</date>
      <program-used>Legere PWA</program-used>
    </document-info>
  </description>
  <body>
`;

  if (sections.length > 0) {
    for (const section of sections) {
      fb2 += `    <section>
      <title>
        <p>${escapeXml(section.title)}</p>
      </title>
`;

      const paragraphs = section.content
        .split('\n\n')
        .filter((p) => p.trim().length > 0);

      for (const para of paragraphs) {
        const paragraphXml = convertParagraphToFb2(para, assetIdMap);
        if (paragraphXml) {
          fb2 += paragraphXml;
        }
      }

      fb2 += `    </section>\n`;
    }
  } else {
    fb2 += `    <section>
`;
    const paragraphs = book.content
      .split('\n\n')
      .filter((p) => p.trim().length > 0 && !p.trim().startsWith('#'));

    for (const para of paragraphs) {
      const paragraphXml = convertParagraphToFb2(para, assetIdMap);
      if (paragraphXml) {
        fb2 += paragraphXml;
      }
    }
    fb2 += `    </section>\n`;
  }

  fb2 += `  </body>
`;

  if (binaryEntries.length > 0) {
    fb2 += `${binaryEntries.join('\n')}\n`;
  }

  fb2 += `</FictionBook>`;

  return fb2;
}

function extractSections(markdown: string): Array<{ title: string; content: string }> {
  const sections: Array<{ title: string; content: string }> = [];
  const lines = markdown.split('\n');

  let currentSection: { title: string; content: string } | null = null;

  for (const line of lines) {
    const headingMatch = line.match(/^#{1,2}\s+(.+)$/);

    if (headingMatch && headingMatch[1]) {
      if (currentSection) {
        sections.push(currentSection);
      }
      currentSection = {
        title: headingMatch[1],
        content: '',
      };
    } else if (currentSection) {
      currentSection.content += line + '\n';
    }
  }

  if (currentSection) {
    sections.push(currentSection);
  }

  return sections;
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function htmlToPlainText(html: string, options?: { headingBreaks?: boolean }): string {
  let text = html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');

  if (options?.headingBreaks) {
    text = text
      .replace(/<h[1-6][^>]*>/gi, '\n\n')
      .replace(/<\/h[1-6]>/gi, '\n');
  }

  text = text
    .replace(/<p[^>]*>/gi, '')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&quot;/gi, '"')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\r\n/g, '\n')
    .replace(/\n\s*\n\s*\n/g, '\n\n');

  return text.trim();
}

async function ensurePdfFonts(doc: any): Promise<void> {
  for (const font of robotoFonts) {
    doc.addFileToVFS(font.file, font.data);
    doc.addFont(font.file, PDF_FONT_NAME, font.style);
  }
}

function prepareImageAssets(book: Book): {
  assetIdMap: Map<string, string>;
  binaryEntries: string[];
  coverImageId: string | null;
} {
  const assetIdMap = new Map<string, string>();
  const binaryEntries: string[] = [];

  const assets = book.bundle?.assets
    ? Object.values(book.bundle.assets)
    : [];

  let index = 0;
  let fallbackCoverId: string | null = null;
  let coverImageId: string | null = null;

  for (const asset of assets) {
    if (!asset || asset.type !== 'image') {
      continue;
    }

    const base64 = assetDataToBase64(asset.data);
    if (!base64) {
      console.warn(`FB2 export: unable to convert asset ${asset.path} to base64`);
      continue;
    }

    const normalizedPath = normalizeAssetPath(asset.path);
    const id = createBinaryId(normalizedPath, index++);

    const keys = new Set<string>();
    keys.add(asset.path);
    keys.add(normalizedPath);

    const decoded = safeDecodeURIComponent(normalizedPath);
    keys.add(decoded);

    const normalizedLower = normalizedPath.toLowerCase();
    keys.add(normalizedLower);
    keys.add(decoded.toLowerCase());

    if (normalizedLower.startsWith('assets/')) {
      const withoutPrefix = normalizedLower.slice('assets/'.length);
      keys.add(withoutPrefix);
    }

    const fileName = normalizedPath.split('/').pop();
    if (fileName) {
      keys.add(fileName);
      keys.add(fileName.toLowerCase());
    }

    for (const key of keys) {
      assetIdMap.set(key, id);
      assetIdMap.set(`./${key}`, id);
      assetIdMap.set(`/${key}`, id);
    }

    binaryEntries.push(`  <binary id="${id}" content-type="${asset.mimeType}">${base64}</binary>`);

    if (!fallbackCoverId) {
      fallbackCoverId = id;
    }

    const lowerPath = normalizedPath.toLowerCase();
    if (!coverImageId && (lowerPath.includes('cover') || lowerPath.includes('облож') || lowerPath.includes('front'))) {
      coverImageId = id;
    }
  }

  if (!coverImageId) {
    coverImageId = fallbackCoverId;
  }

  return { assetIdMap, binaryEntries, coverImageId };
}

function convertParagraphToFb2(paragraph: string, assetIdMap: Map<string, string>): string {
  const trimmed = paragraph.trim();
  if (!trimmed) {
    return '';
  }

  const inlineImages: string[] = [];
  const textWithoutImages = trimmed.replace(/!\[[^\]]*\]\(([^)]+)\)/g, (_match, path) => {
    const assetId = resolveAssetId(path, assetIdMap);
    if (assetId && !inlineImages.includes(assetId)) {
      inlineImages.push(assetId);
    }
    return '';
  }).trim();

  let result = '';

  if (textWithoutImages.length > 0) {
    const converted = convertMarkdownInlineToFb2(textWithoutImages);
    if (converted) {
      result += `      <p>${converted}</p>\n`;
    }
  }

  for (const imageId of inlineImages) {
    result += `      <p><image xlink:href="#${imageId}"/></p>\n`;
  }

  return result;
}

function convertMarkdownInlineToFb2(text: string): string {
  let processed = text.replace(/\r\n/g, '\n');

  processed = processed
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^[-*+]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '');

  const placeholders: Array<{ key: string; value: string }> = [];
  let placeholderIndex = 0;

  const transforms: Array<{ regex: RegExp; wrap: (content: string) => string }> = [
    { regex: /\*\*([^*]+)\*\*/g, wrap: (content) => `<strong>${content}</strong>` },
    { regex: /__([^_]+)__/g, wrap: (content) => `<strong>${content}</strong>` },
    { regex: /(?<!\*)\*([^*]+)\*(?!\*)/g, wrap: (content) => `<emphasis>${content}</emphasis>` },
    { regex: /_([^_]+)_/g, wrap: (content) => `<emphasis>${content}</emphasis>` },
    { regex: /`([^`]+)`/g, wrap: (content) => `<code>${content}</code>` },
  ];

  for (const transform of transforms) {
    processed = processed.replace(transform.regex, (_match, group) => {
      const key = `__FB2_TAG_${placeholderIndex++}__`;
      placeholders.push({
        key,
        value: transform.wrap(escapeXml(group)),
      });
      return key;
    });
  }

  processed = escapeXml(processed);

  for (const { key, value } of placeholders) {
    processed = processed.split(key).join(value);
  }

  return processed.trim();
}

function resolveAssetId(rawPath: string, assetIdMap: Map<string, string>): string | null {
  if (!rawPath) {
    return null;
  }

  const trimmed = rawPath.trim();
  const candidates = new Set<string>();

  candidates.add(trimmed);

  const decoded = safeDecodeURIComponent(trimmed);
  candidates.add(decoded);

  const normalized = normalizeAssetPath(decoded);
  candidates.add(normalized);
  candidates.add(normalized.toLowerCase());

  if (normalized.startsWith('assets/')) {
    const withoutPrefix = normalized.slice('assets/'.length);
    candidates.add(withoutPrefix);
    candidates.add(withoutPrefix.toLowerCase());
  }

  const fileName = normalized.split('/').pop();
  if (fileName) {
    candidates.add(fileName);
    candidates.add(fileName.toLowerCase());
  }

  for (const candidate of candidates) {
    const id = assetIdMap.get(candidate);
    if (id) {
      return id;
    }
  }

  return null;
}

function splitAuthorName(fullName: string): { firstName: string; lastName?: string } {
  const parts = fullName.split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return { firstName: 'Unknown' };
  }

  if (parts.length === 1) {
    return { firstName: parts[0]! };
  }

  const [firstName, ...rest] = parts;
  return {
    firstName: firstName!,
    lastName: rest.join(' '),
  };
}

function normalizeAssetPath(path: string): string {
  return safeDecodeURIComponent(path)
    .replace(/\\/g, '/')
    .replace(/^\.\/+/, '')
    .replace(/^\/+/, '');
}

function createBinaryId(path: string, index: number): string {
  const fileName = path.split('/').pop() || `image_${index}`;
  let id = fileName.replace(/[^a-zA-Z0-9_.-]/g, '_');

  if (!/^[A-Za-z]/.test(id)) {
    id = `img_${index}_${id}`;
  }

  return id;
}

function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function assetDataToBase64(data: unknown): string | null {
  if (data == null) {
    return null;
  }

  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (!trimmed) {
      return null;
    }
    if (trimmed.startsWith('data:')) {
      const commaIndex = trimmed.indexOf(',');
      if (commaIndex !== -1) {
        return trimmed.slice(commaIndex + 1).trim();
      }
    }
    if (isBase64String(trimmed)) {
      return trimmed.replace(/\s+/g, '');
    }
    return stringToBase64(trimmed);
  }

  if (data instanceof ArrayBuffer) {
    return arrayBufferToBase64(data);
  }

  if (ArrayBuffer.isView(data)) {
    const view = data as ArrayBufferView;
    const buffer = view.buffer;
    // Convert SharedArrayBuffer to ArrayBuffer if needed
    if (buffer instanceof ArrayBuffer) {
      return arrayBufferToBase64(buffer);
    } else {
      // SharedArrayBuffer - copy to a new ArrayBuffer
      const bytes = new Uint8Array(view.byteLength);
      bytes.set(new Uint8Array(buffer, view.byteOffset, view.byteLength));
      return arrayBufferToBase64(bytes.buffer);
    }
  }

  if (typeof data === 'object') {
    const record = data as Record<string, unknown>;

    if (record.buffer instanceof ArrayBuffer) {
      return arrayBufferToBase64(record.buffer);
    }

    if (record.type === 'Buffer' && Array.isArray(record.data)) {
      const bytes = Uint8Array.from(record.data as number[]);
      return arrayBufferToBase64(bytes.buffer);
    }
  }

  return null;
}

function isBase64String(value: string): boolean {
  if (value.length < 8 || value.length % 4 !== 0) {
    return false;
  }
  return /^[A-Za-z0-9+/=\s]+$/.test(value);
}

function stringToBase64(value: string): string {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(value, 'utf-8').toString('base64');
  }
  if (typeof TextEncoder !== 'undefined') {
    const encoder = new TextEncoder();
    const bytes = encoder.encode(value);
    return arrayBufferToBase64(bytes.buffer);
  }
  return btoa(value);
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(buffer).toString('base64');
  }

  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary);
}

function escapeXml(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}
